import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Rocket,
  Layers,
  LogIn,
  Menu,
  X,
  Truck,
  Boxes,
  Globe,
  ChevronDown,
} from 'lucide-react';
import logo from '../../static/images/logo_digiqin.png';
import { useModal } from '../../context/ModalContext';
import { useTranslation } from 'react-i18next';


const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openJoinModal } = useModal();

  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openProducts, setOpenProducts] = useState(false);

  const navLinks = [
    { name: 'Overview', path: '/', icon: LayoutDashboard },
    { name: 'Products', icon: Package, hasSub: true },
    { name: 'Features', path: '/features', icon: Layers },
    { name: 'Track Shipment', path: '/track-shipment', icon: Truck },
    { name: 'Join Platform', action: 'join', icon: Rocket },
  ];

  const productLinks = [
    { name: 'DQ Logix', path: '/dq-logix', icon: Truck },
    { name: 'DQ-IMS', path: '/dq-ims', icon: Boxes },
    { name: 'DQ – Website & DM', path: '/website-dm', icon: Globe },
  ];

  const isActive = (path) => location.pathname === path;

  const isProductActive =
    location.pathname.startsWith('/dq-logix') ||
    location.pathname.startsWith('/dq-ims') ||
    location.pathname.startsWith('/website-dm');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`
        fixed top-0 w-full z-50
        transition-all duration-300
        border-b
        ${scrolled
          ? 'bg-white/90 backdrop-blur-md shadow-md border-b border-purple-100'
          : 'bg-white/60 backdrop-blur-sm'}
      `}
    >
      <div className={`container mx-auto px-4 ${scrolled ? 'py-2' : 'py-3'}`}>
        <div className="flex items-center w-full">

          {/* LOGO */}
          <div
            className="cursor-pointer flex items-center shrink-0"
            onClick={() => navigate('/')}
          >
            <img
              src={logo}
              alt="DigiQin"
              className={`${scrolled ? 'h-8 sm:h-9' : 'h-9 sm:h-10'} w-auto max-w-[140px] sm:max-w-[180px] object-contain transition-all duration-300`}
            />
          </div>

          {/* DESKTOP NAV */}
          <div className="hidden lg:flex ml-auto items-center gap-1">

            {navLinks.map((link) => {
              const Icon = link.icon;

              /* PRODUCTS DROPDOWN */
              if (link.hasSub) {
                return (
                  <div
                    key={link.name}
                    className="relative"
                    onMouseEnter={() => setOpenProducts(true)}
                    onMouseLeave={() => setOpenProducts(false)}
                  >
                    <button
                      className={`
                        group relative px-4 py-2 rounded-lg
                        flex items-center gap-3 text-sm font-medium
                        transition-all duration-300
                        ${isProductActive
                          ? 'text-purple-900'
                          : 'text-gray-600 hover:text-purple-800'}
                      `}
                    >
                      <span
                        className={`
                          w-8 h-8 rounded-md flex items-center justify-center
                          transition-all duration-300
                          ${isProductActive ? 'bg-purple-200' : 'group-hover:bg-purple-200'}
                        `}
                      >
                        <Icon className="w-4.5 h-4.5" />
                      </span>

                      Products
                      <ChevronDown className="w-4 h-4" />

                      {/* HOVER / ACTIVE BORDER */}
                      <span
                        className={`
                          absolute left-1/2 -bottom-1 h-[2px]
                          bg-purple-600 transition-all duration-300
                          ${isProductActive
                            ? 'w-6 left-[calc(50%-12px)]'
                            : 'w-0 group-hover:w-6 group-hover:left-[calc(50%-12px)]'}
                        `}
                      />
                    </button>

                    {openProducts && (
                      <div className="absolute top-full left-0 w-72 p-3 bg-white rounded-xl shadow-xl border border-purple-100">
                        {productLinks.map((p, idx) => {
                          const SubIcon = p.icon;
                          return (
                            <div key={p.path}>
                              <button
                                onClick={() => {
                                  navigate(p.path);
                                  setOpenProducts(false);
                                }}
                                className="w-full px-4 py-2 flex items-center gap-3 rounded-md hover:bg-purple-50 text-gray-700"
                              >
                                <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center">
                                  <SubIcon className="w-5 h-5 text-purple-700" />
                                </div>
                                <span className="text-sm font-semibold">
                                  {p.name}
                                </span>
                              </button>
                              {idx !== productLinks.length - 1 && (
                                <div className="mx-4 h-px bg-purple-100" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              /* JOIN PLATFORM POPUP */
              if (link.action === 'join') {
                return (
                  <button
                    key={link.name}
                    onClick={openJoinModal}
                    className="group relative px-4 py-2 rounded-lg flex items-center gap-3 text-sm font-medium text-gray-600 hover:text-purple-800"
                  >
                    <span className="w-8 h-8 rounded-md flex items-center justify-center group-hover:bg-purple-200">
                      <Icon className="w-4.5 h-4.5" />
                    </span>

                    {link.name}

                    {/* HOVER BORDER */}
                    <span
                      className="
                        absolute left-1/2 -bottom-1 h-[2px]
                        bg-purple-600 transition-all duration-300
                        w-0 group-hover:w-6 group-hover:left-[calc(50%-12px)]
                      "
                    />
                  </button>
                );
              }

              /* NORMAL LINKS */
              return (
                <button
                  key={link.name}
                  onClick={() => navigate(link.path)}
                  className={`
                    group relative px-4 py-2 rounded-lg
                    flex items-center gap-3 text-sm font-medium
                    transition-all duration-300
                    ${isActive(link.path)
                      ? 'text-purple-900'
                      : 'text-gray-600 hover:text-purple-800'}
                  `}
                >
                  <span
                    className={`
                      w-8 h-8 rounded-md flex items-center justify-center
                      transition-all duration-300
                      ${isActive(link.path) ? 'bg-purple-200' : 'group-hover:bg-purple-200'}
                    `}
                  >
                    <Icon className="w-4.5 h-4.5" />
                  </span>

                  {link.name}

                  {/* ACTIVE / HOVER BORDER */}
                  <span
                    className={`
                      absolute left-1/2 -bottom-1 h-[2px]
                      bg-purple-600 transition-all duration-300
                      ${isActive(link.path)
                        ? 'w-6 left-[calc(50%-12px)]'
                        : 'w-0 group-hover:w-6 group-hover:left-[calc(50%-12px)]'}
                    `}
                  />
                </button>
              );
            })}
          </div>

          {/* BUSINESS LOGIN */}
          <div className="hidden lg:block ml-4">
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 rounded-full bg-purple-900 text-white text-sm font-semibold flex items-center gap-2 hover:bg-purple-700 transition"
            >
              <LogIn className="w-4 h-4" />
              Business Login
            </button>
          </div>

          {/* MOBILE / IPAD: login sits in the bar, outside the hamburger */}
          <div className="lg:hidden ml-auto flex items-center gap-2">
            <button
              onClick={() => {
                navigate('/login');
                setIsMobileMenuOpen(false);
              }}
              className="px-3 md:px-5 py-2 rounded-full bg-purple-900 text-white text-xs md:text-sm font-semibold flex items-center gap-1.5 hover:bg-purple-700 transition whitespace-nowrap"
            >
              <LogIn className="w-4 h-4 shrink-0" />
              <span className="md:hidden">Login</span>
              <span className="hidden md:inline">Business Login</span>
            </button>

            <button
              className="p-2 rounded-lg bg-white shadow-sm shrink-0"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* MOBILE MENU */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 space-y-2">
            {navLinks.map((link) => {
              const Icon = link.icon;

              if (link.hasSub) {
                return (
                  <div key={link.name} className="space-y-2">
                    <div className="px-4 py-2 text-sm font-medium text-gray-700 flex items-center gap-3">
                      <Icon className="w-4 h-4" />
                      Products
                    </div>
                    <div className="pl-8 space-y-1">
                      {productLinks.map((p) => {
                        const SubIcon = p.icon;
                        return (
                          <button
                            key={p.path}
                            onClick={() => {
                              navigate(p.path);
                              setIsMobileMenuOpen(false);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-600 hover:bg-purple-50 rounded-md flex items-center gap-2"
                          >
                            <SubIcon className="w-4 h-4" />
                            {p.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              if (link.action === 'join') {
                return (
                  <button
                    key={link.name}
                    onClick={() => {
                      openJoinModal();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm font-medium text-gray-600 hover:bg-purple-50 rounded-md flex items-center gap-3"
                  >
                    <Icon className="w-4 h-4" />
                    {link.name}
                  </button>
                );
              }

              return (
                <button
                  key={link.name}
                  onClick={() => {
                    navigate(link.path);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`
                    w-full px-4 py-2 text-left text-sm font-medium rounded-md flex items-center gap-3
                    ${isActive(link.path)
                      ? 'text-purple-900 bg-purple-50'
                      : 'text-gray-600 hover:bg-purple-50'}
                  `}
                >
                  <Icon className="w-4 h-4" />
                  {link.name}
                </button>
              );
            })}

            {/* MOBILE BUSINESS LOGIN */}
            <button
              onClick={() => {
                navigate('/login');
                setIsMobileMenuOpen(false);
              }}
              className="w-full px-4 py-2 rounded-md bg-purple-900 text-white text-sm font-semibold flex items-center gap-2 hover:bg-purple-700 transition mt-4"
            >
              <LogIn className="w-4 h-4" />
              Business Login
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;